import { z } from "zod";

import { type GetImpulseFormInput, ImpulseFormShape, ImpulseFormUnit } from "~/tools/impulse-form";
import { isStringDirty } from "~/tools/is-string-dirty";
import type { Partial } from "~/tools/partial";
import { Template } from "~/tools/template";

type WidgetQueryPromql = ImpulseFormUnit<string, ReadonlyArray<string>, Template<["${", "}"], null>>;
const promqlSchema = z
	.string()
	.trim()
	.min(1, "PromQL cannot be empty")
	.transform((promql) => Template<null>(promql));

type WidgetQueryAlias = ImpulseFormUnit<string, null, undefined | Template<["${", "}"], null>>;

type WidgetQueryMinStep = ImpulseFormUnit<string, null, undefined | Template<["${", "}"], null>>;

type WidgetQuery = ImpulseFormShape<{
	promql: WidgetQueryPromql;
	alias: WidgetQueryAlias;
	minStep: WidgetQueryMinStep;
}>;

const DEFAULT = {
	promql: "",
	alias: "",
	minStep: "",
} satisfies GetImpulseFormInput<WidgetQuery>;

function init(initial?: Partial<GetImpulseFormInput<WidgetQuery>, "deep">): WidgetQuery {
	return ImpulseFormShape({
		promql: ImpulseFormUnit(initial?.promql ?? DEFAULT.promql, {
			schema: promqlSchema,
			isInputDirty: isStringDirty,
		}),

		alias: ImpulseFormUnit(initial?.alias ?? DEFAULT.alias, {
			transform: (input) => {
				const trimmed = input.trim();

				return trimmed ? Template(trimmed) : undefined;
			},
			isInputDirty: isStringDirty,
		}),

		minStep: ImpulseFormUnit(initial?.minStep ?? DEFAULT.minStep, {
			isInputDirty: isStringDirty,
			transform: (input) => {
				const trimmed = input.trim();

				return trimmed ? Template(trimmed) : undefined;
			},
		}),
	});
}

const WidgetQuery = {
	default: DEFAULT,
	init,
} as const;

export { WidgetQuery };
export type { WidgetQueryPromql, WidgetQueryAlias, WidgetQueryMinStep };
